type DeepPartial<T> = T extends (...args: any[]) => any
  ? T
  : T extends readonly (infer U)[]
    ? readonly DeepPartial<U>[]
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  return Object.getPrototypeOf(value) === Object.prototype;
}

function cloneValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item)) as T;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, item]) => {
      result[key] = cloneValue(item);
    });
    return result as T;
  }

  return value;
}

function mergeValue<T>(base: T, override: DeepPartial<T> | undefined): T {
  if (override === undefined) {
    return cloneValue(base);
  }

  if (Array.isArray(override)) {
    return override.map((item) => cloneValue(item)) as T;
  }

  if (isPlainObject(base) && isPlainObject(override)) {
    const result: Record<string, unknown> = cloneValue(base);

    Object.entries(override).forEach(([key, value]) => {
      const baseValue = result[key];
      result[key] = mergeValue(baseValue, value as never);
    });

    return result as T;
  }

  if (isPlainObject(override)) {
    return cloneValue(override as T);
  }

  return override as T;
}

export function mergeChartConfig<T>(
  defaults: T,
  overrides?: DeepPartial<T>,
): T {
  return mergeValue(defaults, overrides);
}
